// This normalises a position value
export const normalisePositionValue = (value) => {

    // This treats undefined as null
    if (value === undefined) {

        // This returns null
        return null;

    }

    // This treats null as null
    if (value === null) {

        // This returns null
        return null;

    }

    // This converts the value to a string
    const stringValue = String(value).trim();

    // This treats empty string as null
    if (stringValue === "") {

        // This returns null
        return null;

    }

    // This treats "null" as null
    if (stringValue.toLowerCase() === "null") {

        // This returns null
        return null;

    }

    // This treats "default" as null
    if (stringValue.toLowerCase() === "default") {

        // This returns null
        return null;

    }

    // This returns the cleaned value
    return stringValue;

};








// This normalises a rotate angle into the 0 to 359 range
export const normaliseRotateDegrees = (value) => {

    // This converts the incoming value to a number
    const parsedValue = Number(value);

    // This returns zero if the value is invalid
    if (Number.isNaN(parsedValue)) {

        // This returns zero
        return 0;

    }

    // This normalises the value into a positive 0-359 angle
    const normalisedValue = ((parsedValue % 360) + 360) % 360;

    // This returns the normalised value
    return normalisedValue;

};



// This snaps a rotate angle to the nearest increment
export const snapRotateDegrees = (value, step = 45) => {

    // This converts the step to a safe number
    const safeStep = Number(step) > 0 ? Number(step) : 45;

    // This normalises the input value first
    const normalisedValue = normaliseRotateDegrees(value);

    // This snaps to the nearest step
    const snappedValue = Math.round(normalisedValue / safeStep) * safeStep;

    // This returns the snapped normalised angle
    return normaliseRotateDegrees(snappedValue);

};



// This rotates clockwise by a fixed step
export const rotateClockwise = (currentRotate, step = 45) => {

    // This converts the current rotate value to a safe number
    const safeCurrentRotate = Number(currentRotate) || 0;

    // This adds the step and snaps the result
    const nextRotate = snapRotateDegrees(safeCurrentRotate + Number(step || 45), step);

    // This returns the new rotate value
    return nextRotate;

};



// This calculates the angle in degrees from a centre point to a pointer point
export const getAngleFromPoints = ({
    centreX,
    centreY,
    pointerX,
    pointerY,
}) => {

    // This gets the horizontal distance from centre to pointer
    const deltaX = Number(pointerX) - Number(centreX);

    // This gets the vertical distance from centre to pointer
    const deltaY = Number(pointerY) - Number(centreY);

    // This converts the angle from radians to degrees
    const angleInDegrees = (Math.atan2(deltaY, deltaX) * 180) / Math.PI;

    // This shifts the angle so zero is at the top instead of the right
    const topBasedAngle = angleInDegrees + 90;

    // This returns a normalised degree value
    return normaliseRotateDegrees(topBasedAngle);

};


// This normalises an angle into the 0 to 359 range
export const normaliseDegrees = (value) => {

    // This converts the incoming value to a number
    const safeValue = Number(value || 0);

    // This returns the normalised angle
    return ((safeValue % 360) + 360) % 360;

};



// This returns the correct diagonal resize cursor for a rotated corner handle
export const getRotatedCornerCursor = ({
    rotate = 0,
    handle = "",
}) => {

    // This normalises the current rotation
    const normalisedRotate = normaliseDegrees(rotate);

    // This works out the base handle angle
    let baseAngle = 0;

    // This maps the handle to its starting corner angle
    if (handle === "tr") {

        // This is the top right corner angle
        baseAngle = 45;

    } else if (handle === "br") {

        // This is the bottom right corner angle
        baseAngle = 135;

    } else if (handle === "bl") {

        // This is the bottom left corner angle
        baseAngle = 225;

    } else {

        // This defaults to top left
        baseAngle = 315;

    }

    // This adds the image rotation to the corner angle
    const rotatedAngle = normaliseDegrees(baseAngle + normalisedRotate);

    // This snaps the rotated angle to the nearest diagonal family
    const snappedIndex = Math.round(rotatedAngle / 45) % 8;

    // These are the 8 directional cursor steps
    const cursorMap = [
        "ns-resize",
        "nesw-resize",
        "ew-resize",
        "nwse-resize",
        "ns-resize",
        "nesw-resize",
        "ew-resize",
        "nwse-resize",
    ];

    // This returns the correct cursor
    return cursorMap[snappedIndex];

};






// This safely converts a numeric value
export const toSafeNumber = (value, fallbackValue) => {

    // This converts the value to a number
    const parsedValue = Number(value);

    // This returns the fallback if invalid
    if (Number.isNaN(parsedValue)) {

        // This returns the fallback
        return fallbackValue;

    }

    // This returns the parsed value
    return parsedValue;

};



// This safely converts a scale value
export const toSafeScaleNumber = (value, fallbackValue = 1) => {

    // This converts the value to a number
    const parsedValue = Number(value);

    // This rejects invalid numbers
    if (Number.isNaN(parsedValue)) {

        // This returns the fallback
        return fallbackValue;

    }

    // This rejects zero and negative scales
    if (parsedValue <= 0) {

        // This returns the fallback
        return fallbackValue;

    }

    // This returns the parsed scale
    return parsedValue;

};



// This checks whether a value is present enough to override inherited data
const hasUsableValue = (value) => {

    // This rejects undefined
    if (value === undefined) {

        // This returns false
        return false;

    }

    // This rejects null
    if (value === null) {

        // This returns false
        return false;

    }

    // This rejects empty strings
    if (typeof value === "string" && value.trim() === "") {

        // This returns false
        return false;

    }

    // This returns true for everything else
    return true;

};



// This returns the last meaningful value from a list
const pickLastMeaningfulValue = (...values) => {

    // This loops backwards so later sources win
    for (let index = values.length - 1; index >= 0; index -= 1) {

        // This reads the current value
        const value = values[index];

        // This returns the first usable value found
        if (hasUsableValue(value)) {

            // This returns that value
            return value;

        }

    }

    // This returns undefined if nothing usable exists
    return undefined;

};



// This creates the default transform
export const getDefaultPlacementTransform = ({ role, fitFill }) => {

    // This returns the default transform object
    return {

        role: String(role || ""),
        fit_fill: String(fitFill || "fit"),
        centre_x: 0.5,
        centre_y: 0.5,
        scale_x: 1,
        scale_y: 1,
        rotation: 0,
        position: null,
        background_colour: null,
        edited: "N",
        edited_background: null,
        edited_position: null,

    };

};


// This resolves the transform field-by-field in priority order
export const resolvePlacementTransform = ({
    role,
    fitFill,
    childRoleData,
    parentRoleData,
    designRoleData,
    childSku,
}) => {

    // This creates the default base
    const defaultTransform = getDefaultPlacementTransform({
        role: role,
        fitFill: fitFill,
    });

    // This creates safe source objects
    const safeDesignRoleData = designRoleData || {};
    const safeParentRoleData = parentRoleData || {};
    const safeChildRoleData = childRoleData || {};

    // This resolves fit/fill in the same design -> parent -> child priority order
    const resolvedFitFill = String(
        pickLastMeaningfulValue(
            defaultTransform.fit_fill,
            safeDesignRoleData?.fit_fill,
            safeParentRoleData?.fit_fill,
            safeChildRoleData?.fit_fill
        ) || "fit"
    );

    // This gets the exact role data only for non-cascading flags
    const ownRoleData = childSku
        ? safeChildRoleData
        : safeParentRoleData;
    
    // This resolves centre x
    const resolvedCentreX = toSafeNumber(
        pickLastMeaningfulValue(
            defaultTransform.centre_x,
            safeDesignRoleData?.centre_x ?? safeDesignRoleData?.center_x,
            safeParentRoleData?.centre_x ?? safeParentRoleData?.center_x,
            safeChildRoleData?.centre_x ?? safeChildRoleData?.center_x
        ),
        0.5
    );

    // This resolves centre y
    const resolvedCentreY = toSafeNumber(
        pickLastMeaningfulValue(
            defaultTransform.centre_y,
            safeDesignRoleData?.centre_y ?? safeDesignRoleData?.center_y,
            safeParentRoleData?.centre_y ?? safeParentRoleData?.center_y,
            safeChildRoleData?.centre_y ?? safeChildRoleData?.center_y
        ),
        0.5
    );

    // This resolves scale x
    const resolvedScaleX = toSafeScaleNumber(
        pickLastMeaningfulValue(
            defaultTransform.scale_x,
            safeDesignRoleData?.scale_x,
            safeParentRoleData?.scale_x,
            safeChildRoleData?.scale_x
        ),
        1
    );

    // This resolves scale y
    const resolvedScaleY = toSafeScaleNumber(
        pickLastMeaningfulValue(
            defaultTransform.scale_y,
            safeDesignRoleData?.scale_y,
            safeParentRoleData?.scale_y,
            safeChildRoleData?.scale_y
        ),
        1
    );

    // This resolves rotation
    const resolvedRotation = toSafeNumber(
        pickLastMeaningfulValue(
            defaultTransform.rotation,
            safeDesignRoleData?.rotation,
            safeParentRoleData?.rotation,
            safeChildRoleData?.rotation
        ),
        0
    );

    // This resolves position
    const resolvedPosition = normalisePositionValue(
        pickLastMeaningfulValue(
            defaultTransform.position,
            normalisePositionValue(safeDesignRoleData?.position),
            normalisePositionValue(safeParentRoleData?.position),
            normalisePositionValue(safeChildRoleData?.position)
        )
    );

    // This resolves background colour
    const resolvedBackgroundColour = pickLastMeaningfulValue(
        defaultTransform.background_colour,
        safeDesignRoleData?.background_colour,
        safeParentRoleData?.background_colour,
        safeChildRoleData?.background_colour
    ) || null;

    // This returns the resolved transform
    return {

        // This stores the role
        role: String(role || ""),

        // This stores resolved fit fill
        fit_fill: resolvedFitFill,

        // This stores resolved centre x
        centre_x: resolvedCentreX,

        // This stores resolved centre y
        centre_y: resolvedCentreY,

        // This stores resolved scale x
        scale_x: resolvedScaleX,

        // This stores resolved scale y
        scale_y: resolvedScaleY,

        // This stores resolved rotate
        rotation: resolvedRotation,

        // This stores resolved position
        position: resolvedPosition,

        // This stores resolved background colour
        background_colour: resolvedBackgroundColour,

        // This stores role's own edited flag only
        //edited: String(ownRoleData?.edited || "N"),


        // This stores role's own edited flag only
        edited:

            (ownRoleData?.centre_x !== null &&
            ownRoleData?.centre_x !== undefined) ||

            (ownRoleData?.centre_y !== null &&
            ownRoleData?.centre_y !== undefined) ||

            (ownRoleData?.scale_x !== null &&
            ownRoleData?.scale_x !== undefined) ||

            (ownRoleData?.scale_y !== null &&
            ownRoleData?.scale_y !== undefined) ||

            (ownRoleData?.rotation !== null &&
            ownRoleData?.rotation !== undefined)

                ? "Y"
                : null,


        // This stores role's own edited background flag only
        edited_background:
            ownRoleData?.background_colour !== null &&
            ownRoleData?.background_colour !== undefined
                ? "Y"
                : null,

        // This stores role's own edited position flag only
        edited_position:
            ownRoleData?.position !== null &&
            ownRoleData?.position !== undefined
                ? "Y"
                : null,

    };

};




// This creates the outgoing payload for product updates
export const buildPlacementEditorPayload = ({
    productSku,
    childSku,
    active,
    currentTransform,
    resolvedTransform,
    changeSource = "init",
}) => {

    // This checks whether this is a hard reset to defaults
    const isResetToDefault = String(changeSource || "") === "reset_to_default";

    // This returns the payload
    return {

        // This stores product sku
        product_sku: String(productSku || ""),

        // This stores child sku
        child_sku: childSku ? String(childSku) : null,

        // This stores role
        role: String(currentTransform?.role || ""),

        // This stores active
        active: active,

        // This stores fit fill
        fit_fill: String(currentTransform?.fit_fill || "fit"),

        // This stores centre x
        centre_x: Number(currentTransform?.centre_x ?? 0.5),

        // This stores centre y
        centre_y: Number(currentTransform?.centre_y ?? 0.5),

        // This stores scale x
        scale_x: Number(currentTransform?.scale_x ?? 1),

        // This stores scale y
        scale_y: Number(currentTransform?.scale_y ?? 1),

        // This stores rotate
        rotation: Number(currentTransform?.rotation ?? 0),

        // This stores position
        position: normalisePositionValue(currentTransform?.position),

        // This stores background colour
        background_colour: currentTransform?.background_colour || null,

        // This preserves the role background edited flag unless reset
        edited:
            isResetToDefault
                ? "N"
                : (currentTransform?.edited || "N"),


        // This preserves the role background edited flag unless reset
        edited_background:
            isResetToDefault
                ? null
                : (currentTransform?.edited_background || null),


         // This preserves the role position edited flag unless reset
        edited_position:
            isResetToDefault
                ? null
                : (currentTransform?.edited_position || null),

        // This can be useful to the caller
        //position_changed: positionChanged ? "Y" : "N",

        // This can be useful to the caller
        //background_changed: backgroundChanged ? "Y" : "N",

        // This stores where the change came from
        changeSource: String(changeSource || "init"),

        // This tells the parent updater to remove saved overrides
        clear_role_data: isResetToDefault ? "Y" : "N",

    };

};



// This returns the save-ready role payload
export const buildSaveableRolePlacement = (roleData) => {

    // This reads edited flag
    const isEdited = String(roleData?.edited || "N") === "Y";

    // This reads position
    const safePosition = normalisePositionValue(roleData?.position);

    // This reads background colour
    const safeBackgroundColour = roleData?.background_colour || null;

    // This checks if we have any placement fields to save
    const shouldIncludePlacementData = isEdited;

    // This checks if position should be saved
    const shouldIncludePosition = safePosition !== null;

    // This checks if background colour should be saved
    const shouldIncludeBackground = safeBackgroundColour !== null;

    // If there is nothing to save, return null
    if (!shouldIncludePlacementData && !shouldIncludePosition && !shouldIncludeBackground) {

        // This returns null
        return null;

    }

    // This returns the final payload
    return {

        // This stores role
        role: String(roleData?.role || ""),

        // This stores active
        active: roleData?.active,

        // This conditionally stores transform data
        ...(shouldIncludePlacementData
            ? {
                centre_x: Number(roleData?.centre_x ?? 0.5),
                centre_y: Number(roleData?.centre_y ?? 0.5),
                scale_x: Number(roleData?.scale_x ?? 1),
                scale_y: Number(roleData?.scale_y ?? 1),
                rotation: Number(roleData?.rotation ?? 0),
                edited: "Y",
                fit_fill: String(roleData?.fit_fill || "fit"),
            }
            : {}),

        // This conditionally stores position
        ...(shouldIncludePosition
            ? {
                position: safePosition,
            }
            : {}),

        // This conditionally stores background colour
        ...(shouldIncludeBackground
            ? {
                background_colour: safeBackgroundColour,
            }
            : {}),

        // This conditionally stores edited background
        ...(shouldIncludeBackground
            ? {
                edited_background: roleData?.edited_background || "Y",
            }
            : {}),

        // This conditionally stores edited position
        ...(shouldIncludePosition
            ? {
                edited_position: roleData?.edited_position || "Y",
            }
            : {}),

    };

};